import { useState, useEffect } from 'react';
import { Clock, BarChart3, Loader2, Settings, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { incidentAPI, serviceRequestAPI, reportAPI } from '../services/api';

const urgColors = { asap: 'bg-rose-100 text-rose-800', this_week: 'bg-amber-100 text-amber-800', standard: 'bg-blue-100 text-blue-800', no_rush: 'bg-slate-200 text-slate-700' };

export default function MySLAPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myIncidents, setMyIncidents] = useState([]);
  const [mySRs, setMySRs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dRes, incRes, srRes] = await Promise.allSettled([
          reportAPI.dashboard(),
          incidentAPI.list({ ordering: '-created_at', page_size: 10 }),
          serviceRequestAPI.list({ ordering: '-created_at', page_size: 10 }),
        ]);
        if (dRes.status === 'fulfilled') setStats(dRes.value.data);
        if (incRes.status === 'fulfilled') setMyIncidents(incRes.value.data?.results || incRes.value.data || []);
        if (srRes.status === 'fulfilled') setMySRs(srRes.value.data?.results || srRes.value.data || []);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const allTickets = [...myIncidents, ...mySRs];
  const metSla = allTickets.filter(t => t.sla_resolution_met === true).length;
  const totalResolved = allTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;
  const breached = allTickets.filter(t => t.sla_resolution_met === false).length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  return (
    <div data-testid="my-sla-page">
      <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
        <Clock className="w-6 h-6 text-sky-500" /> My SLA Dashboard
      </h2>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-sky-500">
          <span className="text-sm text-slate-500 dark:text-slate-400">My Tickets</span>
          <div className="text-3xl font-bold mt-2 dark:text-white">{allTickets.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-emerald-500">
          <span className="text-sm text-slate-500 dark:text-slate-400">Within SLA</span>
          <div className="text-3xl font-bold mt-2 text-emerald-600">{metSla}</div>
          <span className="text-xs text-emerald-600">{totalResolved > 0 ? Math.round(metSla / totalResolved * 100) : 0}% compliance</span>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-rose-500">
          <span className="text-sm text-slate-500 dark:text-slate-400">SLA Breaches</span>
          <div className="text-3xl font-bold mt-2 text-rose-600">{breached}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 shadow-sm border-l-4 border-l-amber-500">
          <span className="text-sm text-slate-500 dark:text-slate-400">Overall Compliance</span>
          <div className="text-3xl font-bold mt-2 dark:text-white">{stats?.sla_compliance || 0}%</div>
        </div>
      </div>

      {/* My Recent Incidents */}
      <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 mb-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2 dark:text-white">
          <AlertCircle className="w-4 h-4 text-rose-500" /> My Recent Incidents
        </h3>
        {myIncidents.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">No incidents raised yet</p>
        ) : (
          <div className="space-y-2">
            {myIncidents.map(t => (
              <div key={t.id} onClick={() => navigate(`/ticket/incident/${t.id}`)}
                className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                <span className="flex items-center gap-2">
                  <span className="font-mono bg-rose-100 text-rose-800 px-2 py-0.5 rounded text-xs font-semibold">{t.ticket_number}</span>
                  <span className="dark:text-white">{t.title}</span>
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${t.sla_resolution_met === false ? 'bg-rose-100 text-rose-800' : t.sla_resolution_met === true ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                  {t.sla_resolution_met === false ? 'Breached' : t.sla_resolution_met === true ? 'Met' : t.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Recent Service Requests */}
      <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700">
        <h3 className="font-semibold mb-4 flex items-center gap-2 dark:text-white">
          <Settings className="w-4 h-4 text-blue-500" /> My Recent Service Requests
        </h3>
        {mySRs.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">No service requests raised yet</p>
        ) : (
          <div className="space-y-2">
            {mySRs.map(sr => (
              <div key={sr.id} onClick={() => navigate(`/ticket/sr/${sr.id}`)}
                className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                <span className="flex items-center gap-2">
                  <span className="font-mono bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">{sr.ticket_number}</span>
                  <span className="dark:text-white">{sr.title}</span>
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${urgColors[sr.urgency_level] || 'bg-slate-100 text-slate-600'}`}>
                  {sr.urgency_level?.replace(/_/g, ' ')} · {sr.status?.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
